import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  downloadInvoiceAttachment,
  downloadInvoiceMatchedDocument,
  useUploadInvoicePdf,
} from '@/features/invoices/data/documents'
import { validateInvoicePdf } from '@/features/invoices/pdf-upload'
import { type ApInvoiceAttachment, type ApInvoiceDetail } from '@/features/invoices/view-models'
import { useInvoiceMatchedDocuments } from '@/features/invoices/queries'
import type { useAuth } from '@/hooks/use-auth'
import { getApiErrorMessage } from '@/lib/api-error'

/** Some supplier emails send PDFs as application/octet-stream or image/pdf; the file name still says .pdf. */
export function isPdfAttachment(attachment: Pick<ApInvoiceAttachment, 'content_type' | 'file_name'>) {
  return attachment.content_type === 'application/pdf' || /\.pdf$/i.test(attachment.file_name)
}

export function useInvoiceDocuments(
  invoiceId: string,
  invoice: ApInvoiceDetail | undefined,
  getAccessToken: ReturnType<typeof useAuth>['getAccessToken'],
) {
  const matchedDocumentsQuery = useInvoiceMatchedDocuments(invoiceId)

  const uploadPdf = useUploadInvoicePdf(invoiceId)

  const [file, setFile] = useState<File | null>(null)

  const [correctedInvoiceUpload, setCorrectedInvoiceUpload] = useState(false)

  const invoiceDocuments = useMemo(() => {
    const unique = new Map<string, ApInvoiceAttachment>()
    for (const attachment of invoice?.attachments ?? []) {
      // A resend of the same file shows once (newest); a statement named like the invoice stays separate.
      const key = `${attachment.document_type ?? 'invoice'}:${attachment.file_name.trim().toLowerCase()}`
      if (!unique.has(key)) unique.set(key, attachment)
    }
    return [...unique.values()]
  }, [invoice?.attachments])
  // Newest first: the newest invoice PDF, not a statement or credit memo saved after it. Bank details and
  // other papers (a W-9) sent with the invoice are never shown as the invoice (USA-1705).
  const billingDocuments = invoiceDocuments.filter(
    (attachment) => !['payment_details', 'other'].includes(attachment.document_type ?? 'invoice'),
  )
  const invoicePdfs = billingDocuments.filter(isPdfAttachment)
  const primaryPdf =
    invoicePdfs.find((attachment) => (attachment.document_type ?? 'invoice') === 'invoice') ||
    invoicePdfs[0] ||
    billingDocuments[0]
  const [primaryPdfUrl, setPrimaryPdfUrl] = useState<string | null>(null)
  const [primaryPdfLoading, setPrimaryPdfLoading] = useState(false)
  const [primaryPdfError, setPrimaryPdfError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    let objectUrl: string | null = null
    setPrimaryPdfUrl(null)
    setPrimaryPdfError(null)
    if (!primaryPdf?.id) {
      setPrimaryPdfLoading(false)
      return () => undefined
    }
    setPrimaryPdfLoading(true)
    void (async () => {
      try {
        const token = await getAccessToken()
        if (!token) throw new Error('Failed to acquire access token')
        const blob = await downloadInvoiceAttachment(token, invoiceId, primaryPdf.id)
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setPrimaryPdfUrl(objectUrl)
      } catch (error) {
        if (active)
          setPrimaryPdfError(getApiErrorMessage(error, 'PDF preview failed'))
      } finally {
        if (active) setPrimaryPdfLoading(false)
      }
    })()
    return () => {
      active = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [getAccessToken, invoiceId, primaryPdf?.id])

  const matchedDocuments = matchedDocumentsQuery.data ?? []

  const downloadMatchedDocument = async (
    downloadPath: string | null | undefined,
    fileName: string,
  ) => {
    if (!downloadPath) {
      toast.error('No immutable file snapshot is available for this document')
      return
    }
    try {
      const token = await getAccessToken()
      if (!token) throw new Error('Authentication is required')
      await downloadInvoiceMatchedDocument(token, downloadPath, fileName)
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Could not download matched document'))
    }
  }

  const submitPdfUpload = () => {
    if (!file) {
      toast.error('Choose a PDF to upload')
      return
    }
    const invalid = validateInvoicePdf(file)
    if (invalid) {
      toast.error(invalid)
      return
    }
    uploadPdf.mutate(
      { file, correctedInvoice: correctedInvoiceUpload },
      {
        onSuccess: () => {
          toast.success(
            correctedInvoiceUpload
              ? 'Corrected invoice added for accounting review'
              : 'PDF attached',
          )
          setFile(null)
          setCorrectedInvoiceUpload(false)
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'PDF upload failed')),
      },
    )
  }

  const downloadAttachment = async (attachmentId: number, fileName: string) => {
    const token = await getAccessToken()
    if (!token) {
      toast.error('Failed to acquire access token')
      return
    }
    try {
      const blob = await downloadInvoiceAttachment(token, invoiceId, attachmentId)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = fileName
      anchor.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'PDF download failed'))
    }
  }
  return {
    matchedDocumentsQuery,
    uploadPdf,
    file,
    setFile,
    correctedInvoiceUpload,
    setCorrectedInvoiceUpload,
    invoiceDocuments,
    primaryPdf,
    primaryPdfUrl,
    primaryPdfLoading,
    primaryPdfError,
    matchedDocuments,
    downloadMatchedDocument,
    submitPdfUpload,
    downloadAttachment,
  }
}
